import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContainerState } from './types';
import {
  IColors,
  ILeague,
  ILeagueMatch,
  ILeagueMatchList,
  IRun,
} from '../../types/types';
import { WSConnectionState } from 'utils/LeagueWebSocketManager/types';

export const initialState: ContainerState = {};

export const slice = createSlice({
  name: 'league',
  initialState,
  reducers: {
    setLeagues(state, action: PayloadAction<ILeague[]>) {
      state.leagues = action.payload;
    },
    setLeague(state, action: PayloadAction<ILeague>) {
      state.league = action.payload;
    },
    setLeagueMatches(state, action: PayloadAction<ILeagueMatchList[]>) {
      state.matches = action.payload;
      if (state.league) state.league.matches = action.payload;
    },
    setLeagueMatch(state, action: PayloadAction<ILeagueMatch>) {
      const updated = action.payload;

      // 1. Normalize teams & players
      const teamsWithExtras = updated.teams.map((team) => ({
        ...team,
        extra: {
          ...(team.extra ?? {}),
          points: team.points ?? 0,
        },
        players: team.players.map((p) => ({
          ...p,
          id: p.nickname,
          extra: {
            ...(p.extra ?? {}),
            colors: team.attrs.colors,
          },
        })),
      }));

      // 2. Build lookup: player composite-id → info
      const playerInfoMap: Record<
        string,
        { name: string; avatar: string | null; colors?: IColors }
      > = {};

      teamsWithExtras.forEach((team) => {
        team.players.forEach((p) => {
          const key = `${p.nickname}_${team.id}`;

          playerInfoMap[key] = {
            name: p.name,
            avatar: p.avatar,
            colors: p.extra?.colors,
          };
        });
      });

      // 3. Add colors + avatar to runs.players
      const racesWithPlayers = (updated.races ?? []).map((race) => {
        const runsWithPlayers = (race.runs ?? []).map((run) => {
          if (!run.player) return run;

          const key = `${run.player.nickname}_${run.player.team.id}`;
          const playerInfo = playerInfoMap[key];

          return {
            ...run,
            player: {
              ...run.player,
              id: run.player.nickname,
              avatar: playerInfo?.avatar ?? run.player.avatar,
              extra: {
                ...(run.player.extra ?? {}),
                colors: playerInfo?.colors,
              },
            },
          };
        });

        // isBest calculation
        const validRuns = runsWithPlayers.filter(
          (r) => typeof r.points === 'number' && !isNaN(r.points),
        );
        const maxPoints =
          validRuns.length > 0
            ? Math.max(...validRuns.map((r) => r.points ?? 0))
            : undefined;

        const runsWithIsBest = runsWithPlayers.map((run) => ({
          ...run,
          player: run.player
            ? {
                ...run.player,
                extra: {
                  ...(run.player.extra ?? {}),
                  isBest:
                    run.status !== 'disqualified' &&
                    run.points !== 0 &&
                    run.points === maxPoints,
                },
              }
            : run.player,
        }));

        return { ...race, runs: runsWithIsBest };
      });

      // 4. Recalculate per-player sumPoints
      const playerPointsMap: Record<string, number> = {};

      racesWithPlayers.forEach((race) => {
        race.runs?.forEach((run) => {
          if (!run.player || typeof run.points !== 'number') return;

          const key = `${run.player.nickname}_${run.player.team.id}`;
          playerPointsMap[key] = (playerPointsMap[key] ?? 0) + run.points;
        });
      });

      const teamsWithPlayerPoints = teamsWithExtras.map((team) => ({
        ...team,
        players: team.players.map((p) => {
          const key = `${p.nickname}_${team.id}`;
          return {
            ...p,
            extra: {
              ...(p.extra ?? {}),
              sumPoints: playerPointsMap[key] ?? 0,
            },
          };
        }),
      }));

      // 5. Save to state
      state.match = {
        ...updated,
        teams: teamsWithPlayerPoints,
        races: racesWithPlayers,
      };
    },
    updateRuns(state, action: PayloadAction<IRun[]>) {
      const runsToUpdate = action.payload;
      if (!state.match?.races || !state.match.teams) return;

      const runMap = Object.fromEntries(runsToUpdate.map((r) => [r.id, r]));

      // Build composite player key → name lookup
      const nicknameToNameMap: Record<string, string> = {};
      state.match.teams.forEach((teamEntry) => {
        teamEntry.players.forEach((p) => {
          if (p.nickname) {
            const key = `${p.nickname}_${teamEntry.id}`;
            nicknameToNameMap[key] = p.name ?? p.nickname;
          }
        });
      });

      // 1. Merge runs
      state.match.races = state.match.races.map((race) => {
        if (!race.runs) return race;

        const updatedRuns = race.runs.map((run) => {
          const incoming = runMap[run.id];
          if (!incoming) return run;

          const merged: IRun = { ...run };

          // Always overwrite these if incoming provides them
          if (incoming.score !== undefined) merged.score = incoming.score;
          if (incoming.duration !== undefined)
            merged.duration = incoming.duration;
          if (incoming.status !== undefined) merged.status = incoming.status;
          if (incoming.points !== undefined) merged.points = incoming.points;

          // Add any missing top-level fields from incoming
          for (const key in incoming) {
            if (!(key in merged)) {
              // @ts-ignore
              merged[key] = (incoming as any)[key];
            }
          }

          // Ensure player.extra remains an object (defensive)
          if (merged.player) {
            const key = `${merged.player.nickname}_${merged.player.team.id}`;

            merged.player = {
              ...merged.player,
              ...(incoming.player ?? {}),
              extra: {
                ...(merged.player.extra ?? {}),
                ...(incoming.player?.extra ?? {}),
                // Add player name from roster
                name: nicknameToNameMap[key] ?? merged.player.nickname,
              },
            };
          }

          return merged;
        });

        // isBest calculation after merging runs
        const validRuns = updatedRuns.filter(
          (r) => typeof r.points === 'number' && !isNaN(r.points),
        );
        const maxPoints =
          validRuns.length > 0
            ? Math.max(...validRuns.map((r) => r.points ?? 0))
            : undefined;

        const runsWithIsBest = updatedRuns.map((run) => ({
          ...run,
          player: run.player
            ? {
                ...run.player,
                extra: {
                  ...(run.player.extra ?? {}),
                  isBest:
                    run.status !== 'disqualified' &&
                    run.points !== 0 &&
                    run.points === maxPoints,
                },
              }
            : run.player,
        }));

        return { ...race, runs: runsWithIsBest };
      });

      // 2. Recalculate total team points from runs only
      const teamPoints: Record<string, number> = {};

      state.match.races.forEach((race) => {
        race.runs?.forEach((run) => {
          const teamId = run.player.team.id;
          if (teamId && typeof run.points === 'number') {
            teamPoints[teamId] = (teamPoints[teamId] ?? 0) + run.points;
          }
        });
      });

      // 3. Update team.extra.points = run.points total
      state.match.teams = state.match.teams.map((teamEntry) => ({
        ...teamEntry,
        extra: {
          ...teamEntry.extra,
          points: teamPoints[teamEntry.id!] ?? teamEntry.extra?.points ?? 0,
        },
      }));

      // 4. Recalculate per-player sumPoints
      const playerPointsMap: Record<string, number> = {};

      state.match.races.forEach((race) => {
        race.runs?.forEach((run) => {
          if (!run.player || typeof run.points !== 'number') return;

          const key = `${run.player.nickname}_${run.player.team.id}`;
          playerPointsMap[key] = (playerPointsMap[key] ?? 0) + run.points;
        });
      });

      state.match.teams = state.match.teams.map((teamEntry) => ({
        ...teamEntry,
        players: teamEntry.players.map((p) => {
          const key = `${p.nickname}_${teamEntry.id}`;
          return {
            ...p,
            extra: {
              ...(p.extra ?? {}),
              sumPoints: playerPointsMap[key] ?? 0,
            },
          };
        }),
      }));
    },
    setIsStreaming(
      state,
      action: PayloadAction<ContainerState['isStreaming']>,
    ) {
      state.isStreaming = action.payload;
    },
    setWSConnectionState(state, action: PayloadAction<WSConnectionState>) {
      state.wsConnectionState = action.payload;
    },
  },
});

export const { actions: leagueActions, reducer } = slice;
